<template>
  <transition name="fade">
    <div
        v-if="showDetailModal"
        class="fixed inset-0 flex items-center justify-center bg-gray-700 bg-opacity-50 z-50 overflow-auto"
        @click.self="closeDetailModal"
    >
      <div class="bg-white p-6 rounded-lg shadow-lg w-3/5 md:w-4/5 lg:w-3/5 max-h-full overflow-auto">
        <h3 class="text-2xl font-semibold text-gray-800 text-center mb-4">Transaction Detail</h3>

        <div class="space-y-4">
          <!-- Basic Info -->
          <div class="border rounded-lg p-4 bg-gray-50">
            <h4 class="font-semibold mb-2 text-gray-700">Basic Info</h4>
            <div v-for="(value, key) in filteredData.basic" :key="key" class="flex justify-between py-1">
              <span class="font-medium text-gray-600">{{ formatKey(key) }}</span>
              <span class="text-gray-800">{{ formatValue(key, value) }}</span>
            </div>
          </div>

          <!-- Account Info -->
          <div v-if="filteredData.account" class="border rounded-lg p-4 bg-gray-50">
            <h4 class="font-semibold mb-2 text-gray-700">Account Info</h4>
            <div v-for="(value, key) in filteredData.account" :key="key" class="flex justify-between py-1">
              <span class="font-medium text-gray-600">{{ formatKey(key) }}</span>
              <span class="text-gray-800">{{ value }}</span>
            </div>
          </div>

          <!-- Beneficiary Info -->
          <div v-if="filteredData.beneficiary" class="border rounded-lg p-4 bg-gray-50">
            <h4 class="font-semibold mb-2 text-gray-700">Beneficiary Info</h4>
            <div v-for="(value, key) in filteredData.beneficiary" :key="key" class="flex justify-between py-1">
              <span class="font-medium text-gray-600">{{ formatKey(key) }}</span>
              <span class="text-gray-800">{{ value }}</span>
            </div>
          </div>

          <!-- Document -->
          <div v-if="filteredData.file_path" class="border rounded-lg p-4 bg-gray-50">
            <h4 class="font-semibold mb-2 text-gray-700">Attached Document</h4>
            <img
                v-if="isImage(filteredData.file_path)"
                :src="getFileUrl(filteredData.file_path)"
                class="max-h-60 rounded border mb-2"
            />
            <a
                :href="getFileUrl(filteredData.file_path)"
                target="_blank"
                class="px-3 py-1 bg-blue-500 text-white text-xs rounded"
            >
              View / Download
            </a>
          </div>
        </div>

        <div class="mt-6 flex justify-end">
          <button
              @click="closeDetailModal"
              class="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";

const props = defineProps({
  showDetailModal: Boolean,
  detailData: Object
});
const emit = defineEmits(["close"]);

function closeDetailModal() {
  emit("close");
}

const filteredData = computed(() => {
  if (!props.detailData) return {};
  const data = props.detailData;

  // حذف value_date و فیلتر فیلدهای null
  const basicKeys = ["id","oper_date","doc_no","description","value","status","transaction_type","currency"];
  const accountKeys = data.account ? ["account_number","account_name","available_balance","accounting_balance","swift"] : [];
  const beneficiaryKeys = data.beneficiary ? ["name","account_number","iban","swift","currency","country","city"] : [];

  return {
    basic: Object.fromEntries(
        basicKeys.map(k => [k, data[k]]).filter(([ v]) => v !== null)
    ),
    account: data.account ? Object.fromEntries(
        accountKeys.map(k => [k, data.account[k]]).filter(([v]) => v !== null)
    ) : null,
    beneficiary: data.beneficiary ? Object.fromEntries(
        beneficiaryKeys.map(k => [k, data.beneficiary[k]]).filter(([v]) => v !== null)
    ) : null,
    file_path: data.file_path
  };
});

const baseUrl = "https://api.bisonib.com";
function getFileUrl(path) {
  return `${baseUrl}/file/transfer_docs/${path.split("/")[1]}/${path.split("/")[2]}`;
}

function isImage(file) {
  return /\.(jpg|jpeg|png|gif|webp)$/i.test(file);
}

// نمایش نام فیلد خوانا
function formatKey(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
}

// نمایش مقدار مخصوص oper_date
function formatValue(key, value) {
  if (key === "oper_date") {
    const d = new Date(value);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }
  return value;
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
